import { IPaginationAlt } from 'src/app/shared/models/pagination.model';
import { TemplateQuestionResponse } from './template.model';

export enum ReviewCycleStatus {
  Draft = 'DRAFT',
  Active = 'ACTIVE',
  Started = 'STARTED',
  Ended = 'ENDED',
}

export enum ReviewCycleAction {
  Edit = 'Edit',
  View = 'View',
  Delete = 'Delete',
  Start = 'Start',
  End = 'End',
}

export enum CycleStageName {
  NAME = 'name',
  PARTICIPANTS_SELECTION = 'participant-selection',
  EMPLOYEE_SELECTION = 'employee-selection',
  TEMPLATE_ASSIGNMENT = 'templates-assignment',
  PHASES_SCHEDULE = 'phases-schedule',
}

export enum ReviewType {
  REVIEW_CYCLE = 'REVIEW',
  SURVEY = 'SURVEY',
  PROBATION_REVIEW = 'PROBATION_REVIEW',
}

export enum ReviewCyclePhaseName {
  PEER_SELECTION = 'PEER_SELECTION',
  REVIEW = 'REVIEW',
  VIEW_PACKET = 'VIEW_PACKET',
  NOT_STARTED = 'N/A',
}

export enum NominationStatus {
  PENDING = 'PENDING',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
}

export enum ReviewStepDisplayName {
  SELF_REVIEW = 'Self Review',
  PEER_REVIEW = 'Peer Review',
  UPWARD_REVIEW = 'Upward Review',
  DOWNWARD_REVIEW = 'Downward Review',
  PROBATION_REVIEW = 'Probation Review',
}

export interface ReviewCycleStage {
  reviewCycleStageId: number;
  order: number;
  stageName: CycleStageName;
}

export interface Stage {
  isCompleted: boolean;
  isSelected: boolean;
  stageNumber: number;
  label?: string;
}

export type StagesByType = Partial<Record<ReviewType, Array<ReviewCycleStage>>>;

export interface ReviewCycle {
  currentPhaseName: ReviewCyclePhaseName;
  reviewCycleId: number;
  cycleName: string;
  status: ReviewCycleStatus;
  type: ReviewType;
  lastStage: number;
  start: string;
  end: string;
  currentPhaseStart?: string;
  currentPhaseEnd?: string;
  hasPacket?: boolean;
  isCycleManager?: boolean;
  isProbationLead?: boolean;
  isSharedViewer?: boolean;
}

export interface Reviewee {
  employeeId?: number;
  id?: number;
  name?: string;
  photo?: string;
  position?: string;
  reviewCycleId?: number;
}

export interface AssignedEmployee {
  employeeId?: number;
  name?: string;
  photo?: string;
  position?: string;
}

export interface StepSelection {
  stepName: string;
  peerSelection: PeerReviewStatusResponse[];
}

export enum ReviewCompleteStatus {
  NOT_STARTED = 'NOT_STARTED',
  PARTIALLY_COMPLETED = 'PARTIALLY_COMPLETED',
  COMPLETED = 'COMPLETED',
}

export interface PeerReviewStatusResponse {
  assignmentId: number;
  assignedEmployee: AssignedEmployee;
  reviewCompletionStatus: ReviewCompleteStatus;
  templateId: number;
  isAutoCreated?: boolean;
  nominationStatus?: NominationStatus;
}

export interface ReviewCyclePeerResponse {
  reviewCycleEmployeeId: number;
  reviewingEmployeeId: number;
}

export type StepsByType<K extends string, V> = {
  [key in K]?: V[];
};

export interface ReviewTemplateStep {
  id: number;
  name: string;
}

export interface ReviewCyclePhase {
  id: number;
  phaseName: ReviewCyclePhaseName;
}

export interface ReviewCycleTemplate {
  id?: number;
  templateId: number;
  stepId: ReviewTemplateStep['id'];
}
export interface CreateReviewName {
  cycleName: string;
  type: ReviewType;
}

export const actionsByStatus: Record<ReviewCycleStatus, ReviewCycleAction[]> = {
  [ReviewCycleStatus.Draft]: [ReviewCycleAction.Edit, ReviewCycleAction.Delete],
  [ReviewCycleStatus.Active]: [
    ReviewCycleAction.Edit,
    ReviewCycleAction.Delete,
    ReviewCycleAction.Start,
  ],
  [ReviewCycleStatus.Started]: [ReviewCycleAction.View, ReviewCycleAction.End],
  [ReviewCycleStatus.Ended]: [ReviewCycleAction.View],
};

export interface ReviewCyclePeerRequest {
  selectedPeerIds: number[];
}

export interface ReviewCycleUserWriteReviewResponse {
  assignmentId: number;
  reviewer: Reviewee;
  reviewed: Reviewee;
  templateId: number;
  nominationStatus: NominationStatus;
  completed: boolean;
}

export interface ReviewCycleReviewer {
  assignmentId: number;
  reviewer: AssignedEmployee;
  nominationStatus: NominationStatus;
  isAutoCreated?: boolean;
}

export interface ReviewCycleGetAllPeersResponse {
  reviewed: Reviewee;
  reviewers: ReviewCycleReviewer[];
}

export enum PeerCountStatus {
  HAS_PENDING_PEERS = 'HAS_PENDING_PEERS',
  NO_PENDING_PEERS = 'NO_PENDING_PEERS',
  NONE = 'NONE',
}

export interface RevieweePeeringInfo {
  reviewer: Reviewee;
  peerCount: number;
  isLead?: boolean;
}

export enum PeerNominationStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  MANDATORY = 'MANDATORY',
}

export interface ReviewCyclePeersResponse {
  reviewed: Reviewee;
  reviewers: IPaginationAlt<Peer>;
  hasPendingPeers: boolean;
}

export interface Peer {
  createdBy: number;
  id: number;
  isReviewCompleted: boolean;
  nominationStatus: PeerNominationStatus;
  reviewer: Reviewee;
  isAutoCreated?: boolean;
}

export interface RevieweeFeedbackInfo {
  reviewer: Reviewee;
  completed: number;
  total: number;
  receivedCompleted: number;
  receivedTotal: number;
  selfCompleted: number;
  selfTotal: number;
  hasCompletedFeedback: boolean;
  isPacketSent?: boolean;
  isCycleManager?: boolean;
  canSendPacket?: boolean;
}

export interface ReviewCycleStepPeers {
  stepName: string;
  peerSelection: ReviewCycleReviewer[];
}

export interface ReviewCycleStepAssignedEmployees {
  stepName: string;
  assignmentId: number;
  peerSelection: AssignedEmployee[];
}

export interface ReviewCycleGetAllPeersByStepsResponse {
  assignedEmployee: AssignedEmployee;
  steps: ReviewCycleStepPeers[];
}

export interface AnswerItemCreateRequest {
  answer: string;
  comment?: string;
  questionId: number;
}

export interface AnswerCreateRequest {
  assignmentId: number;
  answers: AnswerItemCreateRequest[];
}

export interface AnswerUpdateRequest {
  assignmentId: number;
  answers: Array<Answer>;
}

export interface AnswerResponse {
  answers: Array<Answer>;
}

export interface QuestionAnswerResponse {
  answers: Array<Answer>;
  templateQuestions: Array<TemplateQuestionResponse>;
}

export interface MarkTemplateCompletedRequest {
  assignmentId: number;
  complete: boolean;
}

export interface MarkTemplateCompletedResponse {
  completed: boolean;
}

export interface MarkTemplateUncompletedResponse {
  completed: boolean;
}

export interface PeerFeedbackDetails {
  employeeName: string;
  given: PeerReviewStatus[];
  received: PeerReviewStatus[];
  self: PeerReviewStatus[];
}

export interface PeerReviewStatus {
  assignedEmployee: {
    employeeId: number;
    name: string;
    position: string;
    photo: string | null;
  };
  assignmentId: number;
  templateId: number;
  reviewCompletionStatus: string;
  stepName: string;
}

export interface PeerNominationUpdateRequest {
  reviewerId: number;
  revieweeId: number;
  nominationStatus: string;
}

export interface ApproveAllPeersRequest {
  cycleId: number;
  employeeId: number;
  request: PeerNominationUpdateRequest[];
}

export interface Answer {
  id: number;
  answer: string;
  questionId: number;
  comment?: string;
}

export enum ParticipantType {
  FEEDBACK_PARTICIPANT = 'FEEDBACK_PARTICIPANT',
  PROBATION_REVIEWEE = 'PROBATION_REVIEWEE',
  PROBATION_REVIEWER = 'PROBATION_REVIEWER',
}

export interface IReviewCycleEmployeeResponse {
  id: number;
  reviewCycleId: number;
  employeeId: number;
  name: string;
  position: string;
  photo: string;
  type: ParticipantType;
}

export enum ExclusionReason {
  CONTRACTOR = 'CONTRACTOR',
  LESS_THAN_FIVE_MONTHS = 'LESS_THAN_FIVE_MONTHS',
  ON_LEAVE = 'ON_LEAVE',
}

export interface ExcludableParticipant {
  employeeId: number;
  name: string;
  position: string;
  hasAvatar: boolean;
  reason: ExclusionReason;
}

export interface ReviewCyclePdfResponse {
  fileName: string;
  fileContent: string;
  contentType: string;
  contentLength: number;
}
